import React, { memo, useCallback } from 'react';
import { StyleSheet, TouchableOpacity } from 'react-native';
import Animated, { interpolate, useAnimatedStyle } from 'react-native-reanimated';

import { useNavigation } from '@react-navigation/native';

import { CloseIcon } from '@/assets/icons/close';
import FavoriteSelected from '@/assets/icons/favorite-selected';
import FavoriteUnselected from '@/assets/icons/favorite-unselected';
import { Text } from '@/components/TextComponent';
import { Box, Row } from '@/components/lib/components';
import { useFavorites } from '@/feature/betslip-pickem/components/FavoritesProvider';
import { PlayerWithTeamAndEvent } from '@/feature/betslip-pickem/types';
import { useFantasyFullScreenPagerTransition } from '@/feature/circular-pager/FantasyFullScreenPagerTransitionProvider';
import { useScrollableModalContext } from '@/feature/circular-pager/ScrollableModalContextProvider';
import { findEnabledLobbyComponentByOrderingKey } from '@/feature/lobby/hooks/use-lobby-query-variables';
import { DEFAULT_LOBBY_ORDERING, LobbyComponent } from '@/feature/lobby/utils/lobbySections';
import { useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { useUser } from '@/hooks/use-user';
import { designSystem } from '@/styles/styles';
import { isWeb } from '@/utils/constants-platform-specific';
import { formatPlayerName } from '@/utils/format-player-name';

export const PlayerProfileTitle = ({
    playerInfo,
    onClose,
    fromSinglePlayerProfile,
}: {
    playerInfo: PlayerWithTeamAndEvent;
    onClose?: () => void;
    fromSinglePlayerProfile?: boolean;
}) => {
    const { scrollOffset } = useScrollableModalContext();
    const { favoritesIds, addFavorite, removeFavorite } = useFavorites();
    const user = useUser();
    const lobbyComponents = useJurisdictionStore(
        state => state.jurisdictionSettings?.productConfig?.lobbyComponents?.items ?? DEFAULT_LOBBY_ORDERING
    );
    const isFavoritesEnabled = findEnabledLobbyComponentByOrderingKey(lobbyComponents, LobbyComponent.FavoritesPlayers);
    const shouldDisplayFavoriteIcon = !user.isGuest() && isFavoritesEnabled;
    const isFavorite = favoritesIds.includes(playerInfo?.id);

    const addToFavorites = () => {
        addFavorite(playerInfo?.id);
    };

    const removeFromFavorites = () => {
        removeFavorite(playerInfo?.id);
    };

    const headerTitleStyle = useAnimatedStyle(() => {
        return {
            opacity: interpolate(scrollOffset.value, [0, 90, 100], [0, 0, 1]),
        };
    });

    return (
        <Animated.View>
            <Row
                paddingHorizontal={'s16'}
                paddingVertical={'s12'}
                justifyContent={'space-between'}
                alignItems={'center'}
            >
                {!fromSinglePlayerProfile ? (
                    <SyncedSheetCloseButton />
                ) : (
                    <TouchableOpacity style={styles.titleBarButton} onPress={onClose}>
                        <CloseIcon />
                    </TouchableOpacity>
                )}
                <Animated.View style={[styles.titleTextContainer, headerTitleStyle]}>
                    <Text variant={'titleLarge'}>{formatPlayerName(playerInfo, false)}</Text>
                </Animated.View>
                {shouldDisplayFavoriteIcon ? (
                    <Box>
                        <TouchableOpacity
                            style={styles.titleBarButton}
                            onPress={isFavorite ? removeFromFavorites : addToFavorites}
                        >
                            {isFavorite ? (
                                <FavoriteSelected />
                            ) : (
                                <FavoriteUnselected color={designSystem.colors.white} />
                            )}
                        </TouchableOpacity>
                    </Box>
                ) : (
                    <Box style={styles.titleBarButton} />
                )}
            </Row>
        </Animated.View>
    );
};

const SyncedSheetCloseButton = memo(() => {
    const { bottomSheetCircularPagerRef } = useFantasyFullScreenPagerTransition();

    const navigation = useNavigation();

    // on web we navigate back
    // on mobile we close the bottom sheet
    const onClose = useCallback(() => {
        if (isWeb) {
            navigation.goBack();
            return;
        }
        bottomSheetCircularPagerRef.current?.close();
    }, [bottomSheetCircularPagerRef, navigation]);

    return (
        <TouchableOpacity style={styles.titleBarButton} onPress={onClose}>
            <CloseIcon />
        </TouchableOpacity>
    );
});

const styles = StyleSheet.create({
    titleTextContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    titleBarButton: {
        width: 24,
        height: 24,
        justifyContent: 'center',
    },
});
